import { useEffect, useRef, useState } from 'react';
import { PLOT_CONFIG } from './plotTheme';
import './Plot.css';

export interface PlotProps {
  data: unknown[];
  layout: Record<string, unknown>;
  /** Describes the chart for screen readers and for tests. */
  label: string;
}

type PlotlyModule = {
  react: (node: HTMLElement, data: unknown[], layout: unknown, config: unknown) => Promise<unknown>;
  purge: (node: HTMLElement) => void;
};

/**
 * One import for the whole page: a dashboard draws a dozen figures, and each
 * one asking for the module separately would be a dozen resolutions of the same
 * 1 MB chunk.
 */
let plotly: Promise<PlotlyModule> | null = null;

function loadPlotly(): Promise<PlotlyModule> {
  plotly ??= import('plotly.js-basic-dist-min').then(
    (module) => (module.default ?? module) as unknown as PlotlyModule,
  );
  return plotly;
}

/**
 * A Plotly figure, loaded only when a chart is actually on screen.
 *
 * Plotly is ~1 MB, and most of Simple Coffee is forms and tables — so it
 * arrives as its own chunk the first time a dashboard renders, not in the
 * bundle everyone downloads to sign in.
 *
 * The figure itself is `aria-hidden`: a canvas of SVG paths tells a screen
 * reader nothing. Every chart that uses this component pairs it with the table
 * of the same numbers, which is the accessible view.
 */
export function Plot({ data, layout, label }: PlotProps) {
  const node = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  // Redrawing: `react()` updates the figure in place, so a change of data does
  // not tear the chart down. Purging here instead would race the next draw —
  // and in development, where React runs effects twice, it did: the teardown of
  // the first run wiped the figure the second had just drawn.
  useEffect(() => {
    let cancelled = false;
    const element = node.current;
    if (!element) return;

    loadPlotly()
      .then((module) => {
        if (cancelled || !node.current) return undefined;
        return module.react(node.current, data, layout, PLOT_CONFIG);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [data, layout]);

  // Unmounting: Plotly attaches listeners and a resize observer to the node, so
  // leaving one behind leaks for as long as the tab is open.
  useEffect(() => {
    const element = node.current;
    return () => {
      if (element && plotly) void plotly.then((module) => module.purge(element));
    };
  }, []);

  if (failed) {
    // The numbers are still on the page, in the table below every chart.
    return (
      <p className="sc-plot__fallback">
        This chart could not be drawn. The figures are in the table below.
      </p>
    );
  }

  return <div className="sc-plot" ref={node} aria-hidden="true" data-chart={label} />;
}
